'use client';

import { useEffect, useRef, useState } from 'react';
import { getMemberCouponCount, signinWithZzimToken } from '@/utils/actions';
import { SigninResultCGType } from '@/types/graphql/root';
import { sideMenuModalAtom } from '@/atom/modal';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';
import usePreventBodyScroll from '@/app/hooks/usePreventBodyScroll';
import useSideMenuAnimation from '@/app/hooks/useSideMenuAnimation';

export default function SideMenu() {
  const router = useRouter();
  const menuRef = useRef<HTMLElement>(null);

  const sideMenuModal = useAtomValue(sideMenuModalAtom);
  const { isRendered, animationClassName, handleAnimationEnd } = useSideMenuAnimation(sideMenuModal);

  const [signinResult, setSigninResult] = useState<SigninResultCGType | null>(null);
  const [couponCount, setCouponCount] = useState(0);

  usePreventBodyScroll(isRendered);

  useEffect(() => {
    if (!sideMenuModal) return;

    signinWithZzimToken().then((signinResult) => {
      setSigninResult(signinResult ?? null);
    });
    getMemberCouponCount().then((count) => {
      setCouponCount(count);
    });
  }, [sideMenuModal]);

  const handleClose = () => router.back();

  useEffect(() => {
    const element = menuRef.current;
    if (!element) return;

    element.addEventListener('menu-navigate', handleClose);
    return () => {
      element.removeEventListener('menu-navigate', handleClose);
    };
  }, [isRendered]);

  if (!isRendered) return null;

  const user = signinResult?.user;
  const isDev = process.env.NEXT_PUBLIC_ZZIMCAR_URL?.includes('dev');
  const isLoggedIn = !!user;

  return (
    <div className='fixed inset-0 z-20 flex justify-center'>
      <button
        type='button'
        aria-label='메뉴 닫기'
        className={`side-menu-backdrop absolute inset-0 bg-black/50 ${animationClassName}`}
        onClick={handleClose}
      />
      <div className='relative w-full max-w-480pxr h-full overflow-hidden'>
        <nav
          className={`side-menu-drawer absolute right-0 top-0 z-10 flex h-full w-full flex-col bg-white ${animationClassName}`}
          onAnimationEnd={handleAnimationEnd}
        >
          <header className='flex h-56pxr shrink-0 items-center justify-end border-b border-gray-200 px-20pxr'>
            <button type='button' aria-label='메뉴 닫기' className='w-24pxr h-24pxr' onClick={handleClose}>
              <img src='https://front-cdn.zzimcar.com/images/close.svg' alt='close' />
            </button>
          </header>

          <div className='flex-1 overflow-y-auto scrollbar-hide pt-20pxr pb-60pxr'>
            <zzimcar-side-menu
              ref={menuRef}
              {...(isDev ? { 'is-dev': true } : {})}
              {...(isLoggedIn ? { 'is-logged-in': true } : {})}
              member-name={user?.name ?? ''}
              point={signinResult?.pointAmount ?? 0}
              coupon-count={couponCount}
              user-id={user?.email ?? ''}
            />
          </div>
        </nav>
      </div>
    </div>
  );
}
